import { SimpleCustomerDto } from '../../../api/persons/dto/simple-customer.dto'
import { Modal, message } from 'antd'
import ClientSelect from '../../../../components/ClientSelect'
import { Typography, Button } from '@sureco/design-system'
import React, { useEffect, useRef, useState } from 'react'
import {
  createCensusFileFromS3,
  getPresignedUploadUrl,
  uploadCensusFile,
} from '../api/census-enrollment-platform-api-client'
import { useCensusV2Clients } from '../hooks/use-census-v2-clients'
import { formatString } from '../../../../helpers/string'
import DownloadCensusTemplateButton from './DownloadCensusTemplate/DownloadCensusTemplateButton'

function CensusManagementUploadModal({
  open,
  onOk,
  onCancel,
}: {
  open: boolean
  onOk: (uploaded: boolean, result?: unknown) => void
  onCancel: () => void
}) {
  const { clients, error } = useCensusV2Clients()
  if (error) {
    const msg = `Error loading companies: ${error.message}`
    console.error(msg)
    message.error(msg, 5)
  }
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [selectedClient, setSelectedClient] = useState<
    SimpleCustomerDto | undefined
  >()
  const [isUploading, setIsUploading] = useState(false)

  const handleCompanyChange = (value: string) => {
    const c = clients.find((c) => c.id === value)
    setSelectedClient(c)
  }
  useEffect(() => {
    setSelectedFile(null)
  }, [open])

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const openFileDialog = () => {
    fileInputRef.current?.click() // Programmatically open the file dialog
  }

  const formatFile = async (file: File, fileName: string): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (event) => {
        const text = event.target?.result as string
        const rows = text.split('\n')
        const formattedRows = rows.map((row) => {
          const values = row.split(',')
          return values
            .map((value) => {
              return formatString(value)
            })
            .join(',')
        })

        const newFile = new File([formattedRows.join('\n')], fileName, {
          type: 'text/csv',
        })
        resolve(newFile)
      }
      reader.onerror = reject
      reader.readAsText(file)
    })
  }

  const handleUpload = async () => {
    if (!selectedFile || !selectedClient) {
      message.error('Please select a file and a client before uploading.')
      return
    }

    setIsUploading(true)
    try {
      const { name: fileName } = selectedFile

      const { presignedUploadUrl, importPublicKey } =
        await getPresignedUploadUrl({ clientId: selectedClient?.id, fileName })
      if (!presignedUploadUrl) {
        console.error('unable to retrieve presigned upload url')
        message.error('An error occurred. Please try again.')
        return
      }

      const formattedFile = await formatFile(selectedFile, fileName)

      const uploadSuccess = await uploadCensusFile(
        presignedUploadUrl,
        formattedFile
      )
      if (!uploadSuccess) {
        console.error('unable to uploadfile using presigned upload url')
        message.error('An error occurred. Please try again.')
        return
      }
      const result = await createCensusFileFromS3({
        fileName,
        importPublicKey,
        clientPublicKey: selectedClient?.id,
      })
      setSelectedFile(null)
      onOk(true, result)
    } catch (error) {
      console.error(error)
      message.error(`Failed to upload file. Error: ${error}`)
    }

    setIsUploading(false)
  }

  return (
    <Modal
      title="Upload Census"
      open={open}
      onOk={handleUpload}
      onCancel={onCancel}
      okText="Upload CSV"
      confirmLoading={isUploading}
      destroyOnClose
    >
      <div style={{ marginBottom: '1em' }}>
        <DownloadCensusTemplateButton />
      </div>
      <div>
        <Typography size={14}>Company:</Typography>
        <ClientSelect
          companies={clients.map(({ name, id }) => ({
            companyName: name,
            id: id,
          }))}
          defaultCompanyId={selectedClient?.id}
          onChange={handleCompanyChange}
        />
      </div>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      <Button style={{ marginTop: '1em' }} onClick={openFileDialog}>
        Select File
      </Button>
      {selectedFile && <p>Selected File: {selectedFile.name}</p>}
    </Modal>
  )
}

export default CensusManagementUploadModal
